import React, { useState } from 'react';
import {
  faEnvelope,
  faArrowLeft,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import FormInput from '../components/FormInput';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { passwordResetSchema, PasswordResetFormData } from '../schemas';

const ForgotPasswordPage: React.FC = () => {
  const { resetPassword, error, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid }
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema),
    mode: 'onChange',
    defaultValues: {
      email: ''
    }
  });

  const watchedFields = watch();

  const onSubmit = async (data: PasswordResetFormData): Promise<void> => {
    setIsLoading(true);

    try {
      const { error } = await resetPassword(data.email);
      if (!error) {
        setIsSubmitted(true);
      }
    } catch (err) {
      // Error handled by useAuth
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = (): void => {
    navigate('/login');
  };

  if (isSubmitted) {
    return (
      <>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100">
                <img
                  className="w-10 h-10 object-contain"
                  src={currentOrganization?.logo_url || "/logoipsum-custom-logogram.svg"}
                  alt={`${currentOrganization?.name || 'OptoSoft'} Logo`}
                />
              </div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {currentOrganization?.name || 'OptoSoft'}
          </h1>
          <p className="text-gray-600 text-sm">
            {currentOrganization?.slogan || 'Sistema de Gestão Clínica'}
          </p>
        </div>

        {/* Success Message */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <FontAwesomeIcon icon={faPaperPlane} className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Email Enviado!</h2>
            <p className="text-gray-600 text-sm mb-6">
              Se o email informado estiver cadastrado em nosso sistema, você receberá as instruções para redefinir sua senha.
            </p>
            
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              onClick={handleBackToLogin}
              leftIcon={faArrowLeft}
            >
              Voltar ao Login
            </Button>
          </div>

          {/* Footer */}
          <div className="mt-6">
            <p className="text-xs text-gray-500 text-center">
              Desenvolvido por{' '}
              <a
                href="https://um0um.com.br"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                101 Soluções
              </a>
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100">
              <img
                className="w-10 h-10 object-contain"
                src={currentOrganization?.logo_url || "/logoipsum-custom-logogram.svg"}
                alt={`${currentOrganization?.name || 'OptoSoft'} Logo`}
              />
            </div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {currentOrganization?.name || 'OptoSoft'}
        </h1>
        <p className="text-gray-600 text-sm">
          {currentOrganization?.slogan || 'Sistema de Gestão Clínica'}
        </p>
      </div>

      {/* Forgot Password Form */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="mb-5">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Esqueceu sua senha?</h2>
          <p className="text-gray-600 text-sm">Digite seu email para receber as instruções de recuperação</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email Field */}
          <FormInput
            name="email"
            control={control}
            id="email"
            type="email"
            autoComplete="email"
            required
            label="Email"
            leftIcon={faEnvelope}
            placeholder="seu@email.com"
          />

          {/* Error Message */}
          {error && (
            <Alert
              variant="error"
              title="Erro ao enviar"
              message={error}
            />
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading || !isValid || !watchedFields.email}
            leftIcon={!isLoading ? faPaperPlane : undefined}
          >
            {isLoading ? 'Enviando...' : 'Enviar Instruções'}
          </Button>
        </form>

        {/* Back to Login Link */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            Lembrou da senha?{' '}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              Voltar ao login
            </button>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6">
          <p className="text-xs text-gray-500 text-center">
            Desenvolvido por{' '}
            <a
              href="https://um0um.com.br"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              101 Soluções
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;